import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, Check, Clock, XCircle, User, Phone, Car, MapPin, Calendar,
  Users, Briefcase, Plane, MessageCircle, Copy, Star, ArrowLeft, AlertTriangle,
} from "lucide-react";
import "./site/landing.css";
import "./site/tracking.css";
import { loadGoogleMaps } from "../lib/googleMaps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_TIMELINE = [
  { key: "pending_confirmation", label: "Reserva recebida", icon: Clock },
  { key: "confirmed",            label: "Confirmada", icon: Check },
  { key: "assigned",             label: "Motorista atribuído", icon: User },
  { key: "in_progress",          label: "Serviço em curso", icon: Car },
  { key: "completed",            label: "Concluído", icon: ShieldCheck },
];

const STATUS_LABELS = {
  pending_confirmation: "A confirmar (até 1h)",
  pending_payment: "Aguarda pagamento",
  confirmed: "Confirmada — pague agora",
  assigned: "Motorista atribuído",
  in_progress: "Em curso",
  completed: "Concluída",
  cancelled: "Cancelada",
  failed: "Falhou",
};

const PAYMENT_METHOD_LABELS = {
  mbway: "MB WAY",
  multibanco: "Multibanco",
  card: "Cartão",
  apple_pay: "Apple Pay",
  google_pay: "Google Pay",
  pix: "Pix",
};

function formatDT(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-PT", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function BookingTracking() {
  const { reference } = useParams();
  const [sp] = useSearchParams();
  const code = sp.get("code") || "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [booking, setBooking] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!code) {
      setError("missing_code");
      setLoading(false);
      return;
    }
    axios.get(`${API}/public/bookings/${reference}?code=${encodeURIComponent(code)}`)
      .then(r => setBooking(r.data))
      .catch(err => {
        const s = err.response?.status;
        setError(s === 403 ? "invalid_code" : s === 404 ? "not_found" : "generic");
      })
      .finally(() => setLoading(false));
  }, [reference, code]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const copy = () => {
    const doneOk = () => { setCopied(true); setTimeout(() => setCopied(false), 2000); };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(shareUrl).then(doneOk, () => fallbackCopy(shareUrl, doneOk));
    } else {
      fallbackCopy(shareUrl, doneOk);
    }
  };
  const fallbackCopy = (text, onOk) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed"; ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.focus(); ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) onOk();
    } catch (e) { /* silent */ }
  };

  if (loading) {
    return (
      <div className="dt-landing" data-testid="tracking-loading">
        <div className="tk-shell">
          <div className="tk-loading">
            <div className="tk-spinner" />
            <p>A carregar a sua reserva…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dt-landing" data-testid="tracking-error">
        <div className="tk-shell">
          <Link to="/" className="tk-back"><ArrowLeft size={14} /> Voltar</Link>
          <div className="tk-error-card">
            <div className="tk-error-icon"><AlertTriangle size={40} /></div>
            <h1>Reserva não encontrada</h1>
            <p>
              {error === "missing_code"  && "O link está incompleto. Verifique o email de confirmação."}
              {error === "invalid_code"  && "O código de verificação está incorrecto. Verifique o link enviado por email."}
              {error === "not_found"     && "Não encontrámos nenhuma reserva com esta referência."}
              {error === "generic"       && "Ocorreu um erro ao carregar. Tente novamente em alguns instantes."}
            </p>
            <div className="tk-error-meta">Referência: <strong>{reference}</strong></div>
            <Link to="/" className="dt-btn dt-btn--primary" data-testid="tracking-back-home">Voltar ao início</Link>
          </div>
        </div>
      </div>
    );
  }

  const currentIdx = STATUS_TIMELINE.findIndex(s => s.key === booking.status);
  const isCancelled = booking.status === "cancelled" || booking.status === "failed";

  return (
    <div className="dt-landing" data-testid="tracking-page">
      <div className="tk-shell">
        <Link to="/" className="tk-back" data-testid="tracking-back"><ArrowLeft size={14} /> DiTours</Link>

        {/* HEADER */}
        <header className="tk-header">
          <div>
            <div className="tk-eyebrow">Reserva</div>
            <h1 className="tk-ref" data-testid="tracking-reference">{booking.reference}</h1>
            <div className="tk-created">Criada em {formatDT(booking.created_at)}</div>
          </div>
          <div className={`tk-status-pill tk-status--${booking.status}`} data-testid="tracking-status">
            <span className="tk-status-dot" />
            {STATUS_LABELS[booking.status] || booking.status}
          </div>
        </header>

        {/* PARTNER RIBBON */}
        {booking.partner && (
          <div className="tk-partner" data-testid="tracking-partner">
            <ShieldCheck size={14} /> Reserva feita através do <strong>{booking.partner.company_name}</strong>
          </div>
        )}

        {/* TIMELINE */}
        {!isCancelled && (
          <div className="tk-timeline" data-testid="tracking-timeline">
            {STATUS_TIMELINE.map((s, i) => {
              const Icon = s.icon;
              const done = currentIdx >= 0 && i <= currentIdx;
              const active = i === currentIdx;
              return (
                <div key={s.key} className={`tk-step ${done ? "is-done" : ""} ${active ? "is-active" : ""}`}>
                  <div className="tk-step__dot"><Icon size={16} /></div>
                  <div className="tk-step__label">{s.label}</div>
                </div>
              );
            })}
          </div>
        )}
        {isCancelled && (
          <div className="tk-timeline-cancel" data-testid="tracking-cancelled">
            <XCircle size={18} /> Esta reserva foi {booking.status === "cancelled" ? "cancelada" : "recusada por falha de pagamento"}.
          </div>
        )}

        {/* DRIVER CARD */}
        {booking.driver && (
          <section className="tk-driver" data-testid="tracking-driver">
            <div className="tk-driver__avatar">
              {booking.driver.photo_url
                ? <img src={booking.driver.photo_url} alt={booking.driver.name} />
                : <User size={26} />}
            </div>
            <div className="tk-driver__body">
              <div className="tk-driver__label">O seu motorista</div>
              <div className="tk-driver__name">{booking.driver.name}</div>
              {booking.driver.rating && (
                <div className="tk-driver__rating">
                  <Star size={13} fill="currentColor" /> {booking.driver.rating.toFixed(1)}
                </div>
              )}
              {(booking.driver.languages || []).length > 0 && (
                <div className="tk-driver__langs">
                  Fala: {booking.driver.languages.join(", ")}
                </div>
              )}
            </div>
            <a
              href={`tel:${booking.driver.phone_masked}`}
              className="tk-driver__cta"
              data-testid="tracking-driver-call"
            >
              <Phone size={14} /> {booking.driver.phone_masked}
            </a>
          </section>
        )}

        {/* MINI MAP (only for transfers with coords) */}
        {booking.type === "transfer" && booking.pickup_lat && booking.destination_lat && (
          <TripMap
            origin={{ lat: booking.pickup_lat, lng: booking.pickup_lng }}
            destination={{ lat: booking.destination_lat, lng: booking.destination_lng }}
            distanceKm={booking.distance_km}
            durationMin={booking.duration_min}
          />
        )}

        {/* SERVICE DETAILS */}
        <section className="tk-details">
          <div className="tk-details__title">Detalhes do serviço</div>
          <div className="tk-detail-row">
            <div className="tk-detail-ic"><Calendar size={16} /></div>
            <div><span>Data e hora</span><strong>{formatDT(booking.service_datetime)}</strong></div>
          </div>
          <div className="tk-detail-row">
            <div className="tk-detail-ic"><MapPin size={16} /></div>
            <div><span>Origem</span><strong>{booking.pickup_address}</strong></div>
          </div>
          <div className="tk-detail-row">
            <div className="tk-detail-ic"><MapPin size={16} /></div>
            <div><span>Destino</span><strong>{booking.destination_address}</strong></div>
          </div>
          <div className="tk-detail-grid">
            <div className="tk-detail-row">
              <div className="tk-detail-ic"><Users size={16} /></div>
              <div><span>Passageiros</span><strong>{booking.passengers || 1}</strong></div>
            </div>
            <div className="tk-detail-row">
              <div className="tk-detail-ic"><Briefcase size={16} /></div>
              <div><span>Malas</span><strong>{booking.luggage || 0}</strong></div>
            </div>
            <div className="tk-detail-row">
              <div className="tk-detail-ic"><Car size={16} /></div>
              <div><span>Categoria</span><strong>{booking.vehicle_type}</strong></div>
            </div>
            {booking.flight_number && (
              <div className="tk-detail-row">
                <div className="tk-detail-ic"><Plane size={16} /></div>
                <div><span>Voo</span><strong>{booking.flight_number}</strong></div>
              </div>
            )}
          </div>
          {booking.special_requests && (
            <div className="tk-detail-row">
              <div className="tk-detail-ic"><MessageCircle size={16} /></div>
              <div><span>Notas</span><strong>{booking.special_requests}</strong></div>
            </div>
          )}
        </section>

        {/* PAYMENT */}
        <section className="tk-payment">
          <div className="tk-details__title">Pagamento</div>
          <div className="tk-payment__row">
            <div>
              <span>Método</span>
              <strong data-testid="tracking-payment-method">
                {PAYMENT_METHOD_LABELS[(booking.payment.method || "").toLowerCase()] ||
                  (booking.payment.method || "").toUpperCase() || "—"}
              </strong>
            </div>
            <div>
              <span>Estado</span>
              <strong className={`tk-pay-status tk-pay-status--${booking.payment.status}`}>
                {booking.payment.status === "paid" && "Pago"}
                {booking.payment.status === "unpaid" && "Por pagar"}
                {booking.payment.status === "failed" && "Falhou"}
                {!["paid", "unpaid", "failed"].includes(booking.payment.status) && booking.payment.status}
              </strong>
            </div>
            <div>
              <span>Total</span>
              <strong className="tk-pay-total">€{Number(booking.payment.amount || 0).toFixed(2)}</strong>
            </div>
          </div>

          {/* Show payment instructions ONLY once booking is confirmed */}
          {booking.payment.ready && booking.payment.session && booking.payment.status !== "paid" && (
            <div className="tk-pay-instructions" data-testid="tracking-payment-instructions">
              <div className="tk-details__title" style={{ marginTop: "1.25rem", marginBottom: "0.5rem" }}>
                Como pagar
              </div>
              {booking.payment.session.method === "multibanco" && (
                <div className="dt-payment__box">
                  <div><span>Entidade</span><strong>{booking.payment.session.multibanco_entity}</strong></div>
                  <div><span>Referência</span><strong>{booking.payment.session.multibanco_reference}</strong></div>
                  <div><span>Valor</span><strong>€{booking.payment.session.amount}</strong></div>
                </div>
              )}
              {booking.payment.session.method === "mbway" && (
                <div className="dt-payment__box">
                  <div><span>Nº MB WAY</span><strong>{booking.payment.session.mbway_phone}</strong></div>
                  <div><span>Valor</span><strong>€{booking.payment.session.amount}</strong></div>
                </div>
              )}
              {booking.payment.session.method === "card" && booking.payment.session.checkout_url && (
                <a
                  href={booking.payment.session.checkout_url}
                  className="dt-btn dt-btn--primary"
                  target="_blank" rel="noopener noreferrer"
                  data-testid="tracking-pay-card"
                >
                  Pagar com cartão <span style={{ marginLeft: 4 }}>→</span>
                </a>
              )}
              {(booking.payment.session.method === "apple_pay" || booking.payment.session.method === "google_pay") && booking.payment.session.checkout_url && (
                <a
                  href={booking.payment.session.checkout_url}
                  className="dt-btn dt-btn--primary"
                  target="_blank" rel="noopener noreferrer"
                  data-testid={`tracking-pay-${booking.payment.session.method}`}
                >
                  Pagar com {booking.payment.session.method === "apple_pay" ? "Apple Pay" : "Google Pay"} <span style={{ marginLeft: 4 }}>→</span>
                </a>
              )}
              {booking.payment.session.method === "pix" && (
                <div className="dt-payment__box" data-testid="tracking-pay-pix">
                  {booking.payment.session.pix_qr && (
                    <div style={{ textAlign: "center", marginBottom: "0.75rem" }}>
                      <img
                        src={booking.payment.session.pix_qr}
                        alt="QR Code Pix"
                        style={{ maxWidth: 180, width: "100%", borderRadius: 8, background: "#fff", padding: 6 }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </div>
                  )}
                  {booking.payment.session.pix_copy_paste && (
                    <div>
                      <span>Pix Copia e Cola</span>
                      <strong style={{ display: "block", fontSize: "0.72rem", wordBreak: "break-all", marginTop: 4 }}>
                        {booking.payment.session.pix_copy_paste}
                      </strong>
                      <button
                        type="button"
                        className="dt-btn dt-btn--ghost"
                        style={{ marginTop: "0.5rem" }}
                        onClick={() => {
                          navigator.clipboard?.writeText(booking.payment.session.pix_copy_paste);
                        }}
                        data-testid="tracking-pix-copy"
                      >
                        Copiar código Pix
                      </button>
                    </div>
                  )}
                  <div style={{ marginTop: "0.5rem" }}>
                    <span>Valor</span><strong>€{booking.payment.session.amount}</strong>
                  </div>
                </div>
              )}
              <p className="tk-pay-hint">{booking.payment.session.instructions}</p>
              {booking.payment.session.simulated && (
                <p className="tk-pay-sim" data-testid="tracking-payment-simulated">
                  ⓘ Modo de teste — pagamento simulado até IfthenPay ser configurado.
                </p>
              )}
            </div>
          )}

          {/* Awaiting confirmation notice */}
          {booking.status === "pending_confirmation" && (
            <div className="tk-pay-awaiting" data-testid="tracking-awaiting">
              ⏱ Ainda estamos a confirmar a sua reserva. Assim que um motorista aceitar, o link de pagamento aparece aqui.
            </div>
          )}
        </section>

        {/* SHARE + HELP */}
        <div className="tk-footer-actions">
          <button
            type="button"
            className="dt-btn dt-btn--ghost"
            onClick={copy}
            data-testid="tracking-copy-link"
          >
            <Copy size={14} /> {copied ? "Copiado!" : "Copiar link"}
          </button>
          <a
            href={`mailto:reservas@ditours.pt?subject=Reserva ${booking.reference}`}
            className="dt-btn dt-btn--ghost"
            data-testid="tracking-contact"
          >
            <MessageCircle size={14} /> Precisa de ajuda?
          </a>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// TripMap — mini Google Map showing origin → destination with route polyline
// -----------------------------------------------------------------------------
function TripMap({ origin, destination, distanceKm, durationMin }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    let cancelled = false;
    loadGoogleMaps().then((google) => {
      if (cancelled || !containerRef.current) return;
      const map = new google.maps.Map(containerRef.current, {
        zoom: 12,
        center: { lat: (origin.lat + destination.lat) / 2, lng: (origin.lng + destination.lng) / 2 },
        disableDefaultUI: true,
        zoomControl: true,
        styles: [
          { elementType: "geometry", stylers: [{ color: "#0f172a" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#0f172a" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#94a3b8" }] },
          { featureType: "poi", stylers: [{ visibility: "off" }] },
          { featureType: "transit", stylers: [{ visibility: "off" }] },
          { featureType: "water", stylers: [{ color: "#1e293b" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#334155" }] },
          { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#1e293b" }] },
        ],
      });

      // Route
      const svc = new google.maps.DirectionsService();
      const renderer = new google.maps.DirectionsRenderer({
        map,
        suppressMarkers: true,
        polylineOptions: { strokeColor: "#f59e0b", strokeWeight: 4, strokeOpacity: 0.9 },
      });
      svc.route({
        origin, destination,
        travelMode: google.maps.TravelMode.DRIVING,
      }).then((res) => {
        if (cancelled) return;
        renderer.setDirections(res);
      }).catch(() => {});

      // Custom markers
      new google.maps.Marker({
        map, position: origin,
        label: { text: "A", color: "#0a0f1e", fontWeight: "bold" },
        icon: {
          path: google.maps.SymbolPath.CIRCLE, scale: 14,
          fillColor: "#f59e0b", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2,
        },
        title: "Origem",
      });
      new google.maps.Marker({
        map, position: destination,
        label: { text: "B", color: "#fff", fontWeight: "bold" },
        icon: {
          path: google.maps.SymbolPath.CIRCLE, scale: 14,
          fillColor: "#0a0f1e", fillOpacity: 1, strokeColor: "#f59e0b", strokeWeight: 2,
        },
        title: "Destino",
      });
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [origin.lat, origin.lng, destination.lat, destination.lng]);

  return (
    <section className="tk-map-section" data-testid="tracking-map">
      <div className="tk-details__title">Percurso</div>
      <div ref={containerRef} className="tk-map" />
      {(distanceKm || durationMin) && (
        <div className="tk-map-meta">
          {distanceKm && <span>📏 {Number(distanceKm).toFixed(1)} km</span>}
          {durationMin && <span>⏱ {Math.round(Number(durationMin))} min</span>}
        </div>
      )}
    </section>
  );
}
